import React, { useEffect, useMemo, useState } from 'react'
import Star from '~/components/common/Star'
import { TextBlockToolTip } from '~/components/common/TextBlock'
import { Button, Dialog, Icon, MenuSelect, Table } from 'bs-unified-ui'
import { Stack, Typography } from '@mui/material'
import { COMMENT_STATUS } from '~/constants/comments'
import commentApi from '~/services/apis/comment'
import { showDialog } from '~/components/common/Dialog'
import { dialogParams } from '~/components/common/Dialog/CustomDialog'
import { BulkActions } from './BulkActions'
import { CommentProps } from './types'
import { CommentForm } from './CommentForm'
import { showToast } from '~/components/common/Toast'
import { decodeHtmlEntities, convertHtmlToPlaintext } from './utils'
import { set } from 'date-fns'

interface CommentTableProps {
  data: CommentProps[]
  loading: boolean
  status: string
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  rowsPerPage: number
  fetchCommentList: () => void
  setLoading: (loading: boolean) => void
  selected: string[]
  setSelected: (ids: string[]) => void
  updateFilter: (filters: Record<string, any>) => void
}

export const CommentTable: React.FC<CommentTableProps> = ({
  status,
  data,
  loading,
  fetchCommentList,
  setLoading,
  selected = [],
  setSelected = () => {},
  updateFilter,
  ...props
}) => {
  const [openedComment, setOpenedComment] = useState<CommentProps | null>(null)
  const [deleteId, setDeleteId] = useState<string>('')

  const refetchDataAfterSuccess = () => {
    if (
      (data?.length === 1 || data?.length === selected.length) &&
      props.page > 1
    ) {
      updateFilter({ page: null })
      return
    }

    fetchCommentList()
  }

  const changeStatus = async (review, newStatus) => {
    setLoading(true)
    try {
      const newReview = {
        ...review,
        status: newStatus,
      }
      await commentApi.update(newReview)
      showToast('Changes saved')
      refetchDataAfterSuccess()
    } catch (e) {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  const deletePermanently = async (id) => {
    setLoading(true)
    try {
      await commentApi.remove(id)
      showToast('Changes saved')
      refetchDataAfterSuccess()
    } catch (e) {
      setLoading(false)
      showDialog(dialogParams.error(e))
    } finally {
      setLoading(false)
      setDeleteId('')
    }
  }

  useEffect(() => {
    setSelected([])
  }, [status])

  const renderMenuSelect = (options) => (
    <MenuSelect
      options={options}
      onSelect={(
        _: unknown,
        opt?: { label: string; value: string; onClick: () => void },
      ) => {
        opt?.onClick?.()
        setSelected([])
      }}
      renderElement={
        <Button data-testid="btn-open-more-menu" variant="text" iconOnly>
          <Icon name="more" type="fill" />
        </Button>
      }
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      menuItemSx={{
        px: 2,
        minWidth: 120,
        '.menu-icon': { display: 'none' },
      }}
    />
  )

  const renderActions = (item) => {
    switch (item.status) {
      case COMMENT_STATUS.PENDING:
        return (
          <Stack direction="row" spacing={1} sx={{ whiteSpace: 'nowrap' }}>
            <Button
              variant="text"
              startIconName="check"
              onClick={() => {
                setSelected([])
                changeStatus(item, COMMENT_STATUS.APPROVED)
              }}
            >
              Publish
            </Button>
            <Button
              variant="text"
              startIconName="spam-3"
              onClick={() => {
                setSelected([])
                changeStatus(item, COMMENT_STATUS.SPAM)
              }}
            >
              Spam
            </Button>
          </Stack>
        )
      case COMMENT_STATUS.APPROVED:
        return renderMenuSelect([
          {
            label: 'Unpublish',
            value: 'unpublish',
            onClick: () => changeStatus(item, COMMENT_STATUS.PENDING),
          },
          {
            label: 'Move to spam',
            value: 'move_to_spam',
            onClick: () => changeStatus(item, COMMENT_STATUS.SPAM),
          },
        ])
      case 'spam':
        return renderMenuSelect([
          {
            label: 'Move to review',
            value: 'unpublish',
            onClick: () => changeStatus(item, COMMENT_STATUS.PENDING),
          },
        ])
      case 'trash':
        return renderMenuSelect([
          {
            label: 'Restore',
            value: 'unpublish',
            onClick: () => changeStatus(item, COMMENT_STATUS.PENDING),
          },
          {
            label: 'Delete permanently',
            value: 'delete_permanently',
            onClick: () => setDeleteId(item?.id),
          },
        ])
      default:
        return null
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'review',
        title: 'Comment',
        renderComponent: (item: CommentProps) => {
          const comment = convertHtmlToPlaintext(item.review)

          return (
            <TextBlockToolTip
              title={<span>{comment}</span>}
              tooltipProps={{}}
              refToolTip={null}
              maxLine={3}
            >
              <Typography
                variant="fs14"
                {...(status === COMMENT_STATUS.TRASH
                  ? {}
                  : {
                      sx: {
                        '&:hover': {
                          textDecoration: 'underline',
                          cursor: 'pointer',
                        },
                      },
                      onClick: () => {
                        setSelected([])
                        setOpenedComment(item)
                      },
                    })}
              >
                {comment}
              </Typography>
            </TextBlockToolTip>
          )
        },
      },
      {
        id: 'rating',
        title: 'Rating',
        width: '15%',
        renderComponent: (item: CommentProps) => (
          <Star value={item.rating} readOnly />
        ),
      },
      {
        id: 'reviewer',
        title: 'Customer',
        width: '15%',
      },
      {
        id: 'product_name',
        title: 'Product',
        width: '15%',
        renderComponent: (item: CommentProps) => (
          <a
            href={item.product_permalink || '#'}
            target="_blank"
            rel="noreferrer"
          >
            <Typography variant="fs14" sx={{ textDecoration: 'underline' }}>
              {decodeHtmlEntities(item.product_name)}
            </Typography>
          </a>
        ),
      },
      {
        id: 'actions',
        title: '',
        width: status === COMMENT_STATUS.PENDING ? '160px' : '32px',
        renderComponent: (item: CommentProps) => renderActions(item),
      },
    ],
    [data, loading, renderActions],
  )

  return (
    <>
      <Table
        outlined={false}
        columns={columns}
        data={data}
        loading={loading}
        stickyHeader
        selected={selected}
        onSelect={(ids) => setSelected(ids)}
        pageStart={1}
        {...props}
        sx={{
          flex: 1,
          overflow: 'hidden',
          '.MuiTableCell-root:first-child': {
            width: '30px',
          },
          '.MuiFormControlLabel-root': {
            mr: 0,
          },
          '& th': {
            backgroundColor: '#FBFBFC',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
            py: 0,

            '.MuiCheckbox-root': {
              p: 0.5,
            },
            '.MuiFormControlLabel-root': {
              ml: '-7px',
            },
          },
        }}
      />

      <BulkActions
        selected={selected}
        clearSelected={() => setSelected([])}
        loading={loading}
        setLoading={setLoading}
        status={status}
        refetchData={() => {
          setSelected([])
          refetchDataAfterSuccess()
        }}
      />

      <CommentForm
        data={openedComment}
        onClose={() => setOpenedComment(null)}
        refetchData={fetchCommentList}
        setLoading={setLoading}
      />

      <Dialog
        title="Delete permanently"
        maxWidth={336}
        open={!!deleteId}
        onClose={() => setDeleteId('')}
        renderFooter={
          <>
            <Button variant="text" onClick={() => setDeleteId('')}>
              Cancel
            </Button>
            <Button
              color="error"
              onClick={() => deletePermanently(deleteId)}
              loading={loading}
            >
              Delete
            </Button>
          </>
        }
      >
        <Typography variant="fs14">
          Once deleted, comment cannot be recovered.
          <br />
          Are you sure you want to continue?
        </Typography>
      </Dialog>
    </>
  )
}
